import { useEffect, useState } from 'react';
import type { EntradaCrearEmpresa } from '@copathy/shared';
import { ErrorApi } from '../lib/api';
import {
  useCrearSector,
  useGuardarCliente,
  useLogoCliente,
  useSectores,
  type Cliente,
  type ClienteListado,
} from '../lib/clientes';
import { componerTelefono, descomponerTelefono, DIAL_POR_DEFECTO } from '../lib/paises';
import { usePuede } from '../lib/auth';
import { Boton } from './Boton';
import { Campo } from './Campo';
import { Modal } from './Modal';
import { SelectorPais, SelectorTelefono } from './CamposPais';
import { Combo } from './Combo';
import { CampoLogo } from './LogoCliente';

/**
 * Alta y edición de un cliente.
 *
 * Vive en `componentes/` porque lo usan dos pantallas: el listado —para
 * registrar— y el detalle —para editar—.
 *
 * ── El orden de los campos no es casual ──
 * Identidad primero (quién es), después contexto (dónde y de qué tamaño), después
 * a quién llamar. Es el orden en que Copathy tiene la información cuando cierra
 * un cliente, no el orden en que están las columnas de la tabla.
 */
type Formulario = {
  nombre: string;
  rif: string;
  pais: string | null;
  sectorId: number | null;
  empleados: string;
  contactoNombre: string;
  contactoCorreo: string;
  dial: string;
  telefono: string;
};

const VACIO: Formulario = {
  nombre: '',
  rif: '',
  pais: null,
  sectorId: null,
  empleados: '',
  contactoNombre: '',
  contactoCorreo: '',
  dial: DIAL_POR_DEFECTO,
  telefono: '',
};

function desdeCliente(c: ClienteListado | Cliente): Formulario {
  const { dial, numero } = descomponerTelefono(c.contactoTelefono);
  return {
    nombre: c.nombre,
    rif: c.rif ?? '',
    pais: c.pais,
    sectorId: c.sectorId,
    empleados: c.empleados === null ? '' : String(c.empleados),
    contactoNombre: c.contactoNombre ?? '',
    contactoCorreo: c.contactoCorreo ?? '',
    dial,
    telefono: numero,
  };
}

export function FormularioCliente({
  objetivo,
  alCerrar,
  alFallar,
  alGuardar,
}: {
  objetivo: ClienteListado | Cliente | 'nuevo' | null;
  alCerrar: () => void;
  alFallar: (mensaje: string | null) => void;
  alGuardar?: (cliente: Cliente) => void;
}) {
  const esNuevo = objetivo === 'nuevo';
  const [datos, setDatos] = useState<Formulario>(VACIO);
  const [inicial, setInicial] = useState<Formulario>(VACIO);
  const [campos, setCampos] = useState<Record<string, string>>({});
  const [logo, setLogo] = useState<File | null>(null);
  const [quitarElLogo, setQuitarElLogo] = useState(false);

  const { crear, editar } = useGuardarCliente();
  const { subir, quitar } = useLogoCliente();
  const sectores = useSectores(objetivo !== null);
  const crearSector = useCrearSector();
  const puedeCrearSector = usePuede('empresas.crear');

  // Se rellena al ABRIRSE, no al construirse: el modal vive montado todo el
  // tiempo, así que sin esto conservaría lo del cliente anterior.
  useEffect(() => {
    if (objetivo === null) return;
    const inicio = objetivo === 'nuevo' ? VACIO : desdeCliente(objetivo);
    setDatos(inicio);
    setInicial(inicio);
    setCampos({});
    setLogo(null);
    setQuitarElLogo(false);
  }, [objetivo]);

  const cambio = (k: keyof Formulario) => (e: { target: { value: string } }) =>
    setDatos((d) => ({ ...d, [k]: e.target.value }));

  const hayCambios =
    JSON.stringify(datos) !== JSON.stringify(inicial) || logo !== null || quitarElLogo;
  const enviando = crear.isPending || editar.isPending || subir.isPending;

  // Texto vacío = «sin dato». Al crear se omite; al editar se manda `null`, que
  // borra — y es distinto de no mandar el campo, que no toca nada.
  const opcional = (v: string) => (v.trim() === '' ? null : v.trim());

  async function guardar() {
    alFallar(null);
    setCampos({});

    const empleados = datos.empleados.trim() === '' ? null : Number(datos.empleados);
    if (empleados !== null && !Number.isInteger(empleados)) {
      setCampos({ empleados: 'Escribe un número entero.' });
      return;
    }

    const telefono = componerTelefono(datos.dial, datos.telefono);

    try {
      let guardado: Cliente;

      if (esNuevo) {
        const cuerpo: EntradaCrearEmpresa = { nombre: datos.nombre.trim() };
        if (opcional(datos.rif)) cuerpo.rif = datos.rif.trim();
        if (datos.pais) cuerpo.pais = datos.pais;
        if (datos.sectorId !== null) cuerpo.sectorId = datos.sectorId;
        if (empleados !== null) cuerpo.empleados = empleados;
        if (opcional(datos.contactoNombre)) cuerpo.contactoNombre = datos.contactoNombre.trim();
        if (opcional(datos.contactoCorreo)) cuerpo.contactoCorreo = datos.contactoCorreo.trim();
        if (telefono) cuerpo.contactoTelefono = telefono;
        guardado = await crear.mutateAsync(cuerpo);
      } else if (objetivo) {
        guardado = await editar.mutateAsync({
          id: objetivo.id,
          cambios: {
            nombre: datos.nombre.trim(),
            rif: opcional(datos.rif),
            pais: datos.pais,
            sectorId: datos.sectorId,
            empleados,
            contactoNombre: opcional(datos.contactoNombre),
            contactoCorreo: opcional(datos.contactoCorreo),
            contactoTelefono: telefono || null,
          },
        });
      } else {
        return;
      }

      /*
        El logotipo se sube DESPUÉS y aparte, y no es un capricho: al registrar
        todavía no existe el id contra el que subir. Y va en su propia petición
        porque es `multipart`, mientras que el resto del formulario es JSON —
        mezclarlos obligaría a que todo el módulo hable multipart.

        Si la imagen falla, el cliente ya quedó guardado. Se avisa de eso en vez
        de fingir que no pasó nada: lo que se perdió es el logo, no el alta.
      */
      if (logo) {
        try {
          guardado = await subir.mutateAsync({ id: guardado.id, archivo: logo });
        } catch (e) {
          alFallar(
            e instanceof ErrorApi
              ? `Se guardó el cliente, pero el logotipo no: ${e.message}`
              : 'Se guardó el cliente, pero el logotipo no se pudo subir.',
          );
        }
      } else if (quitarElLogo && !esNuevo) {
        guardado = await quitar.mutateAsync(guardado.id);
      }

      alCerrar();
      alGuardar?.(guardado);
    } catch (e) {
      if (e instanceof ErrorApi) {
        if (e.campos) setCampos(e.campos);
        else alFallar(e.message);
      } else {
        alFallar('No pudimos guardar. Intenta de nuevo.');
      }
    }
  }

  const yaTieneLogo = objetivo !== null && objetivo !== 'nuevo' && 'tieneLogo' in objetivo
    ? objetivo.tieneLogo && !quitarElLogo
    : false;

  return (
    <Modal
      titulo={esNuevo ? 'Registrar cliente' : 'Editar cliente'}
      ancho="ancho"
      abierto={objetivo !== null}
      hayCambios={hayCambios}
      alCerrar={alCerrar}
      pie={
        <>
          <Boton variante="secundario" onClick={alCerrar}>
            Cancelar
          </Boton>
          <Boton
            onClick={() => void guardar()}
            cargando={enviando}
            disabled={datos.nombre.trim().length < 2}
          >
            {esNuevo ? 'Registrar' : 'Guardar'}
          </Boton>
        </>
      }
    >
      <div className="space-y-5">
        <CampoLogo
          archivo={logo}
          urlActual={yaTieneLogo}
          nombre={datos.nombre}
          alElegir={setLogo}
          alQuitar={() => setQuitarElLogo(true)}
          error={campos.logo}
        />

        <div className="border-t border-marino-100 pt-5">
          <Campo
            etiqueta="Nombre del cliente"
            value={datos.nombre}
            onChange={cambio('nombre')}
            error={campos.nombre}
            autoFocus
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            etiqueta="Identificación fiscal"
            value={datos.rif}
            onChange={cambio('rif')}
            error={campos.rif}
          />
          <SelectorPais
            etiqueta="País"
            iso={datos.pais}
            alCambiar={(iso) => setDatos((d) => ({ ...d, pais: iso }))}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Combo
            etiqueta="Sector"
            opciones={(sectores.data ?? []).map((s) => ({ clave: s.id, texto: s.nombre }))}
            valor={datos.sectorId}
            alCambiar={(id) => setDatos((d) => ({ ...d, sectorId: id === null ? null : Number(id) }))}
            // Solo quien puede registrar clientes puede crear el sector que falta.
            // Sin permiso, el combo no ofrece crear nada.
            {...(puedeCrearSector
              ? {
                  alCrear: async (nombre: string) => {
                    const s = await crearSector.mutateAsync(nombre);
                    return { clave: s.id, texto: s.nombre };
                  },
                }
              : {})}
            vacio="Sin especificar"
            error={campos.sectorId}
            sinCoincidencias="Ningún sector coincide."
          />
          <Campo
            etiqueta="Número de empleados"
            type="number"
            min={0}
            value={datos.empleados}
            onChange={cambio('empleados')}
            error={campos.empleados}
          />
        </div>

        <div className="border-t border-marino-100 pt-5">
          <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-marino-400">
            Contacto en el cliente
          </p>
          <div className="space-y-4">
            <Campo
              etiqueta="Nombre del contacto"
              value={datos.contactoNombre}
              onChange={cambio('contactoNombre')}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                etiqueta="Correo"
                type="email"
                value={datos.contactoCorreo}
                onChange={cambio('contactoCorreo')}
                error={campos.contactoCorreo}
              />
              <SelectorTelefono
                etiqueta="Teléfono"
                dial={datos.dial}
                numero={datos.telefono}
                alCambiar={(dial, numero) => setDatos((d) => ({ ...d, dial, telefono: numero }))}
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
